import { RefObject, useEffect, useState } from "react";

/**
 * The page's scroll container for `el`: the outermost ancestor that scrolls its content. The app
 * scrolls inside its layout, not the document, and a module's own content box (also `overflow: auto`)
 * must not count: the element would always be "in view" of that box.
 */
const pageScroller = (el: Element): Element | null => {
    let found: Element | null = null;
    for (let parent = el.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
        const { overflowY } = getComputedStyle(parent);
        if (overflowY === "auto" || overflowY === "scroll") found = parent;
    }
    return found;
};

/**
 * The box that scrolls `el` right now: the nearest ancestor that scrolls its content and has more of
 * it than fits. On a phone the Market Pulse page scrolls inside its module's content box, under a
 * root layout that barely scrolls at all: an observer rooted at the root layout never saw the lower
 * row until it was on screen, since `rootMargin` widens the root, not the clip of the box in between,
 * so the row mounted in view and grew there as its data came in. With nothing scrolling (a one-screen
 * desktop), the page's scroll container as above.
 */
const nearestScroller = (el: Element): Element | null => {
    for (let parent = el.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
        const { overflowY } = getComputedStyle(parent);
        if ((overflowY === "auto" || overflowY === "scroll") && parent.scrollHeight > parent.clientHeight + 1) return parent;
    }
    return pageScroller(el);
};

/**
 * True once the element comes within `margin` of the visible part of the page, and from then on.
 *
 * For blocks that render a lot but are not on screen when the page starts: on a phone the stock
 * overview's stamps (each with a chart) and its news list sit one to three screens below the
 * fold, and rendering them during start-up was a large share of the page's blocking time. A block
 * that is on screen sees `true` after its first frame. Without IntersectionObserver (very old
 * browsers) it is `true` at once. While `enabled` is false nothing is observed and it stays false.
 *
 * `scroller` is the observer's root, which `margin` widens: the page's scroll container (the
 * default), or the nearest box that scrolls the element (`"nearest"`, for a block inside a module
 * whose content box is what scrolls on a phone, see `nearestScroller`).
 */
export const useNearViewport = (ref: RefObject<Element | null>, { margin = "300px", enabled = true, scroller = "page" }: { margin?: string; enabled?: boolean; scroller?: "page" | "nearest" } = {}): boolean => {
    const [near, setNear] = useState(false);

    useEffect(() => {
        if (near || !enabled) return;
        const el = ref.current;
        if (typeof IntersectionObserver === "undefined" || !el) {
            setNear(true);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setNear(true);
                    observer.disconnect();
                }
            },
            { root: scroller === "nearest" ? nearestScroller(el) : pageScroller(el), rootMargin: margin }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [near, enabled, ref, margin, scroller]);

    return near;
};
